// ==========================================================================================
// NOMBRE: src/components/AIAssistant.js
// VERSIÓN: 5.0 - Links clicables, formato WhatsApp, subida de imágenes
// ==========================================================================================

import React, { useState, useEffect, useRef } from 'react';
import {
  Box, TextField, Paper, Typography, Avatar,
  CircularProgress, IconButton, Button, Fade, Chip, Tooltip
} from '@mui/material';
import SendIcon        from '@mui/icons-material/Send';
import PersonIcon      from '@mui/icons-material/Person';
import WhatsAppIcon    from '@mui/icons-material/WhatsApp';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SmartToyIcon    from '@mui/icons-material/SmartToy';
import BuildIcon       from '@mui/icons-material/Build';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import InventoryIcon   from '@mui/icons-material/Inventory';
import DescriptionIcon from '@mui/icons-material/Description';
import RefreshIcon     from '@mui/icons-material/Refresh';
import AttachFileIcon  from '@mui/icons-material/AttachFile';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const generarSessionId = () =>
  `sim_${Date.now()}_${Math.random().toString(36).slice(2, 8)}@s.whatsapp.net`;

// ── Renderiza texto con formato WhatsApp: *negrita*, links clicables, saltos de línea ──
function RenderTexto({ texto, colorTexto = '#333' }) {
  if (!texto) return null;

  // 1. Separar por URLs
  const URL_RE = /https?:\/\/[^\s)>\]]+/g;
  const segmentos = [];
  let ultimo = 0;
  let m;
  while ((m = URL_RE.exec(texto)) !== null) {
    if (m.index > ultimo) segmentos.push({ tipo: 'texto', val: texto.slice(ultimo, m.index) });
    segmentos.push({ tipo: 'url', val: m[0] });
    ultimo = m.index + m[0].length;
  }
  if (ultimo < texto.length) segmentos.push({ tipo: 'texto', val: texto.slice(ultimo) });

  // 2. Dentro de cada segmento de texto, procesar *negrita* y saltos de línea
  const renderSegTexto = (txt, keyBase) => {
    const partes = txt.split(/(\*[^*\n]+\*)/g);
    return partes.map((p, i) => {
      if (p.startsWith('*') && p.endsWith('*') && p.length > 2) {
        return <strong key={`${keyBase}-b${i}`}>{p.slice(1, -1)}</strong>;
      }
      // Saltos de línea
      return p.split('\n').reduce((acc, line, li, arr) => {
        acc.push(<span key={`${keyBase}-l${i}-${li}`}>{line}</span>);
        if (li < arr.length - 1) acc.push(<br key={`${keyBase}-br${i}-${li}`} />);
        return acc;
      }, []);
    });
  };

  return (
    <Typography variant="body2" sx={{ lineHeight: 1.6, color: colorTexto, wordBreak: 'break-word' }}>
      {segmentos.map((seg, i) =>
        seg.tipo === 'url' ? (
          <a key={i} href={seg.val} target="_blank" rel="noopener noreferrer"
            style={{ color: colorTexto === 'white' ? '#cce5ff' : '#1565c0', fontWeight: 500 }}>
            {seg.val}
          </a>
        ) : renderSegTexto(seg.val, i)
      )}
    </Typography>
  );
}

export default function AIAssistant() {
  const [conversation, setConversation] = useState([]);
  const [inputMsg, setInputMsg]         = useState('');
  const [isTyping, setIsTyping]         = useState(false);
  const [aiStatus, setAiStatus]         = useState(null);
  const [bloqueado, setBloqueado]       = useState(false);

  const sessionIdRef   = useRef(generarSessionId());
  const messagesEndRef = useRef(null);
  const inputRef       = useRef(null);
  const fileInputRef   = useRef(null);

  useEffect(() => {
    fetch(`${API_URL}/api/ai-brain/status`)
      .then(r => r.json())
      .then(d => { if (d.ok) setAiStatus(d.status); })
      .catch(() => {});
    llamarBackend('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation, isTyping]);

  const resetSimulacion = () => {
    sessionIdRef.current = generarSessionId();
    setConversation([]);
    setInputMsg('');
    setIsTyping(false);
    setBloqueado(false);
    llamarBackend('');
  };

  // ── Llamada al backend ────────────────────────────────────────────────────────
  const llamarBackend = async (message, imageBase64 = null, imageMime = null) => {
    setIsTyping(true);
    try {
      const body = { sessionId: sessionIdRef.current, message };
      if (imageBase64) { body.imageBase64 = imageBase64; body.imageMime = imageMime || 'image/jpeg'; }

      const response = await fetch(`${API_URL}/api/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();

      if (data.ok && data.answers) {
        // Imágenes de producto ANTES del texto
        for (const img of (data.imagenesProducto || [])) {
          if (img.url) {
            setConversation(prev => [...prev, { role: 'assistant', content: img.caption || '', imagen: img.url, toolsUsed: [] }]);
            await new Promise(r => setTimeout(r, 300));
          }
        }
        for (const answer of data.answers) {
          await new Promise(r => setTimeout(r, 400));
          setConversation(prev => [...prev, {
            role: 'assistant', content: answer.texto || '',
            imagen: answer.imagen, opciones: answer.opciones,
            toolsUsed: data.herramientas_usadas || []
          }]);
        }
        if (data.type === 'consent_rejected') setBloqueado(true);
      } else {
        setConversation(prev => [...prev, { role: 'assistant', content: 'Tuve un error de conexión. ¿Puedes intentar de nuevo?' }]);
      }
    } catch {
      setConversation(prev => [...prev, { role: 'assistant', content: 'Error de red. Verifica que el servidor esté encendido.' }]);
    }
    setIsTyping(false);
  };

  // ── Enviar texto ──────────────────────────────────────────────────────────────
  const sendMessage = async (text) => {
    const msg = (text || inputMsg).trim();
    if (!msg || bloqueado) return;
    setConversation(prev => [...prev, { role: 'user', content: msg }]);
    setInputMsg('');
    await llamarBackend(msg);
  };

  // ── Enviar desde botón (permite responder aunque esté bloqueado — para botones de consentimiento) ──
  const sendFromButton = async (value) => {
    const msg = value.trim();
    if (!msg) return;
    setBloqueado(false);
    setConversation(prev => [...prev, { role: 'user', content: msg }]);
    await llamarBackend(msg);
  };

  // ── Subir imagen ──────────────────────────────────────────────────────────────
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const dataUrl   = ev.target.result;
      const base64    = dataUrl.split(',')[1];
      const mime      = file.type;
      // Mostrar preview de la imagen en el chat
      setConversation(prev => [...prev, { role: 'user', content: '', imagen: dataUrl }]);
      await llamarBackend('', base64, mime);
    };
    reader.readAsDataURL(file);
  };

  // ── UI helpers ────────────────────────────────────────────────────────────────
  const getToolIcon  = t => ({ buscar_producto: <InventoryIcon fontSize="inherit" />, crear_cotizacion: <DescriptionIcon fontSize="inherit" />, buscar_cliente: <PersonIcon fontSize="inherit" />, enviar_whatsapp: <WhatsAppIcon fontSize="inherit" />, consultar_eventos_dia: <CalendarMonthIcon fontSize="inherit" /> }[t] || <BuildIcon fontSize="inherit" />);
  const getToolLabel = t => ({ buscar_producto: 'Catálogo', crear_cotizacion: 'Cotización', buscar_cliente: 'CRM', enviar_whatsapp: 'WhatsApp', consultar_eventos_dia: 'Calendario' }[t] || t);

  const renderOptions = (options) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
      {options.map((opt, i) => (
        <Button key={i} variant="outlined" size="small"
          startIcon={opt.url?.includes('pdf') ? <PictureAsPdfIcon /> : opt.url?.includes('wa') ? <WhatsAppIcon /> : null}
          onClick={() => opt.url ? window.open(opt.url, '_blank') : sendFromButton(opt.value || opt.label)}
          sx={{ borderRadius: '20px', textTransform: 'none', borderColor: '#1565c0', color: '#1565c0', bgcolor: 'white', '&:hover': { bgcolor: '#e3f2fd' } }}
        >{opt.label}</Button>
      ))}
    </Box>
  );

  const renderToolsBadges = (toolsUsed) => {
    if (!toolsUsed?.length) return null;
    return (
      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
        {toolsUsed.map((t, i) => (
          <Tooltip key={i} title={`Herramienta: ${t}`} arrow>
            <Chip icon={getToolIcon(t)} label={getToolLabel(t)} size="small" variant="outlined"
              sx={{ fontSize: '0.65rem', height: 20, borderColor: '#90caf9', color: '#1976d2', '& .MuiChip-icon': { fontSize: '0.75rem' } }} />
          </Tooltip>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '85vh', maxWidth: '600px', margin: '0 auto', bgcolor: '#f5f5f5', borderRadius: 2, overflow: 'hidden', boxShadow: 3 }}>

      {/* HEADER */}
      <Box sx={{ p: 2, bgcolor: '#1565c0', color: 'white', display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar src="https://www.inflablesdasilva.com/wp-content/uploads/2026/02/WhatsApp-Image-2026-02-16-at-6.45.52-AM-1.jpeg"
          variant="rounded" sx={{ bgcolor: 'white', width: 50, height: 50, p: 0.5 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>José - Asesor Virtual</Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            {aiStatus?.servicio
              ? `✅ IA Activa · ${aiStatus.herramientas_disponibles || 0} herramientas · ${aiStatus.productos_catalogo || 0} productos`
              : '🟡 Modo básico'}
          </Typography>
        </Box>
        {aiStatus?.servicio && (
          <Chip icon={<SmartToyIcon />} label="IA" size="small"
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} />
        )}
        <Tooltip title="Nueva simulación — reiniciar desde cero" arrow>
          <IconButton onClick={resetSimulacion} size="small"
            sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* MENSAJES */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, bgcolor: '#e8eaf6' }}>
        {conversation.map((msg, index) => (
          <Fade in key={index} timeout={300}>
            <Box sx={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', alignItems: 'flex-end', gap: 1 }}>
              {msg.role === 'assistant' && (
                <Avatar sx={{ width: 32, height: 32, bgcolor: '#1565c0' }}
                  src="https://www.inflablesdasilva.com/wp-content/uploads/2026/02/WhatsApp-Image-2026-02-16-at-6.45.52-AM-1.jpeg" />
              )}
              <Box sx={{ maxWidth: '82%' }}>
                <Paper sx={{
                  p: 1.5,
                  borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  bgcolor: msg.role === 'user' ? '#1565c0' : 'white',
                  color: msg.role === 'user' ? 'white' : '#333', boxShadow: 1
                }}>
                  {/* Texto con formato WhatsApp */}
                  {msg.content && (
                    <RenderTexto texto={msg.content} colorTexto={msg.role === 'user' ? 'white' : '#333'} />
                  )}
                  {/* Imagen adjunta */}
                  {msg.imagen && (
                    <Box component="img" src={msg.imagen} alt="imagen"
                      sx={{ mt: msg.content ? 1 : 0, maxWidth: '100%', borderRadius: 1, display: 'block' }} />
                  )}
                  {/* Opciones */}
                  {msg.opciones?.length > 0 && renderOptions(msg.opciones)}
                </Paper>
                {msg.role === 'assistant' && renderToolsBadges(msg.toolsUsed)}
              </Box>
              {msg.role === 'user' && (
                <Avatar sx={{ width: 32, height: 32, bgcolor: '#37474f' }}>
                  <PersonIcon fontSize="small" />
                </Avatar>
              )}
            </Box>
          </Fade>
        ))}

        {isTyping && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: '#1565c0' }}
              src="https://www.inflablesdasilva.com/wp-content/uploads/2026/02/WhatsApp-Image-2026-02-16-at-6.45.52-AM-1.jpeg" />
            <Paper sx={{ p: 1.5, borderRadius: '16px 16px 16px 4px', bgcolor: 'white' }}>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">José está consultando...</Typography>
              </Box>
            </Paper>
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* INPUT */}
      <Box sx={{ p: 1.5, bgcolor: 'white', display: 'flex', gap: 1, borderTop: '1px solid #e0e0e0', alignItems: 'center' }}>
        {bloqueado ? (
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            Chat bloqueado — cliente rechazó el tratamiento de datos. Presiona ↺ para reiniciar.
          </Typography>
        ) : (
          <>
            {/* Botón adjuntar imagen */}
            <input type="file" accept="image/*" ref={fileInputRef} style={{ display: 'none' }} onChange={handleImageUpload} />
            <Tooltip title="Enviar imagen (simula foto desde WhatsApp)" arrow>
              <IconButton onClick={() => fileInputRef.current?.click()} disabled={isTyping} size="small"
                sx={{ color: '#64748b', '&:hover': { color: '#1565c0' } }}>
                <AttachFileIcon />
              </IconButton>
            </Tooltip>

            <TextField fullWidth size="small"
              placeholder="Escribe tu mensaje..."
              value={inputMsg}
              onChange={(e) => setInputMsg(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              inputRef={inputRef}
              disabled={isTyping}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '24px', bgcolor: '#f5f5f5' } }}
            />
            <IconButton onClick={() => sendMessage()} disabled={isTyping || !inputMsg.trim()}
              sx={{ bgcolor: '#1565c0', color: 'white', width: 44, height: 44, flexShrink: 0, '&:hover': { bgcolor: '#0d47a1' }, '&.Mui-disabled': { bgcolor: '#e0e0e0' } }}>
              <SendIcon />
            </IconButton>
          </>
        )}
      </Box>
    </Box>
  );
}
